import React from 'react';
import { Button } from '@/components/ui/button';
import { FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { Image as ImageIcon, X } from 'lucide-react';
import { toast } from 'sonner';
import { useUploadVoucherImage } from '@/api/vouchers/vouchers';
import { getApiErrorMessage } from '@/lib/api-error';

interface ImageUrlsInputProps {
  value: string[];
  onChange: (urls: string[]) => void;
  maxImages: number;
}

const ImageUrlsInput: React.FC<ImageUrlsInputProps> = ({ value, onChange, maxImages }) => {
  const [imageUrls, setImageUrls] = React.useState<string[]>(value || []);
  const [isDragging, setIsDragging] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const uploadImageMutation = useUploadVoucherImage();
  const [isUploading, setIsUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setImageUrls(value || []);
  }, [value]);

  const sanitizeUrls = (urls: string[]) => urls.filter(url => url.trim() !== '');

  const commit = (urls: string[]) => {
    setImageUrls(urls);
    onChange(sanitizeUrls(urls));
  };

  const appendUrl = (url: string) => {
    setImageUrls(prev => {
      const next = [...prev, url];
      onChange(sanitizeUrls(next));
      return next;
    });
  };

  const handleFiles = async (files: FileList | File[]) => {
    if (isUploading) return;
    const availableSlots = maxImages - imageUrls.length;
    if (availableSlots <= 0) return;

    const fileArray = Array.from(files).slice(0, availableSlots);
    if (fileArray.length === 0) return;

    setUploadError(null);
    setIsUploading(true);

    try {
      for (const file of fileArray) {
        const response = await uploadImageMutation.mutateAsync({
          data: { file },
        });

        if (response?.url) {
          appendUrl(response.url);
        }
      }
    } catch (error) {
      const message = getApiErrorMessage(error, 'Bild konnte nicht hochgeladen werden.');
      setUploadError(message);
      toast.error(message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleAddImage = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveImage = (index: number) => {
    const newUrls = imageUrls.filter((_, i) => i !== index);
    commit(newUrls);
  };

  const tryExtractUrlFromDataTransfer = (dt: DataTransfer): string | null => {
    const uriList = dt.getData('text/uri-list');
    if (uriList && /^https?:\/\//i.test(uriList.trim())) return uriList.trim();
    const plain = dt.getData('text/plain');
    if (plain && /^https?:\/\//i.test(plain.trim())) return plain.trim();
    return null;
  };

  const handleDrop: React.DragEventHandler<HTMLDivElement> = async e => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      await handleFiles(e.dataTransfer.files);
      return;
    }

    const url = tryExtractUrlFromDataTransfer(e.dataTransfer);
    if (url) {
      if (imageUrls.length >= maxImages) return;
      commit([...imageUrls, url]);
    }
  };

  const handlePaste: React.ClipboardEventHandler<HTMLDivElement> = e => {
    const text = e.clipboardData.getData('text/plain');
    if (text && /^https?:\/\//i.test(text.trim())) {
      if (imageUrls.length >= maxImages) return;
      commit([...imageUrls, text.trim()]);
      e.preventDefault();
    }
  };

  const fileNameFromUrl = (url: string): string => {
    try {
      const u = new URL(url);
      const last = u.pathname.split('/').filter(Boolean).pop();
      return last || url;
    } catch {
      return url;
    }
  };

  return (
    <div className='space-y-4'>
      <input
        type='file'
        accept='image/*'
        multiple
        ref={fileInputRef}
        className='hidden'
        onChange={event => {
          if (event.target.files) {
            handleFiles(event.target.files);
          }
          event.target.value = '';
        }}
      />
      <div
        className={[
          'bg-swd-hellblau border border-swd-tuerkis-darker border-dashed rounded-xl p-6 flex flex-col items-center gap-4 text-center select-none',
          isDragging ? 'ring-2 ring-swd-tuerkis' : '',
        ].join(' ')}
        onDragOver={e => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onPaste={handlePaste}
        role='region'
        aria-label='Bild hierher ziehen oder URL einfügen'
      >
        <div className='text-swd-tuerkis-dark'>
          <ImageIcon className='h-8 w-8' />
        </div>
        <div className='space-y-1 text-center'>
          <p className='text-sm tracking-wide text-neutral-dunkelgrau'>
            <span className='font-bold text-swd-tuerkis-darker'>Ziehen</span>
            <span> Sie ein Bild herein oder </span>
            <span className='font-bold text-swd-tuerkis-darker'>laden</span>
            <span> Sie es hoch</span>
          </p>
          <p className='text-xs text-neutral-mittelgrau'>Maximale Dateigröße: 120 MB</p>
          <p className='text-xs font-semibold text-neutral-dunkelgrau'>
            (max. {maxImages} {maxImages === 1 ? 'Bild' : 'Bilder'})
          </p>
        </div>
        <Button
          type='button'
          className='btn-base btn-small btn-enabled btn-small-text w-full'
          onClick={handleAddImage}
          disabled={imageUrls.length >= maxImages || isUploading}
        >
          {isUploading ? 'Lädt …' : 'Datei auswählen'}
        </Button>
        {uploadError && <p className='text-xs text-rose-600'>{uploadError}</p>}
      </div>

      {imageUrls.length > 0 && (
        <div className='space-y-2'>
          {imageUrls.map((url, index) => (
            <div key={index} className='flex items-center justify-between'>
              <div className='flex items-center gap-2 min-w-0'>
                <ImageIcon className='h-5 w-5 text-swd-tuerkis' />
                <span className='text-xs font-semibold text-neutral-dunkelgrau truncate'>{fileNameFromUrl(url)}</span>
              </div>
              <Button
                type='button'
                variant='ghost'
                size='icon'
                onClick={() => handleRemoveImage(index)}
                aria-label='Entfernen'
              >
                <X className='h-4 w-4 text-neutral-dunkelgrau' />
              </Button>
            </div>
          ))}
        </div>
      )}

      {imageUrls.length < maxImages && imageUrls.length === 0 && (
        <div className='sr-only'>
          {/* Accessible hint for keyboard users to add first URL via button above */}
          Es sind noch keine Bilder hinzugefügt.
        </div>
      )}
    </div>
  );
};

interface ImageUrlsFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  label?: string;
  description?: string;
  maxImages?: number;
}

export function ImageUrlsField<TFieldValues extends FieldValues>({
  control,
  name,
  label = 'Images',
  maxImages = 5,
}: ImageUrlsFieldProps<TFieldValues>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <div className='bg-white border border-neutral-hellgrau rounded-xl p-6 flex flex-col gap-6'>
            <FormLabel className='text-h3 flex items-center gap-2'>
              <ImageIcon className='h-5 w-5 text-swd-tuerkis-darker' />
              {label}
            </FormLabel>

            <ImageUrlsInput value={field.value || []} onChange={field.onChange} maxImages={maxImages} />
            <FormMessage />
          </div>
        </FormItem>
      )}
    />
  );
}
